import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, Star, Crown, Loader2, Users } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";
import { PRICING } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREE_FEATURES = [
  "1 personalized bedtime story",
  "10–14 illustrations, your chosen style",
  "Voice narration preview",
  "All 11 adventure themes",
];

const PREMIUM_FEATURES = [
  "Unlimited nightly stories",
  "Full voice narration (20+ min)",
  "All 4 narrator voices",
  "Priority story generation",
  "Up to 2 child profiles",
  "Story archive & history",
];

const FAMILY_FEATURES = [
  "Everything in Premium",
  "Unlimited child profiles",
  "Shared \"Family Favorites\" playlist",
  "Perfect for 2+ kids",
];

const TIER_META = {
  premium: {
    label: "Premium",
    eyebrow: "DreamWeaver Unlimited",
    monthlyKey: "dreamweaver_monthly",
    yearlyKey: "dreamweaver_yearly",
    monthlyPrice: "$7",
    yearlyPrice: "$63",
    planIdField: { monthly: "monthly_plan_id", yearly: "yearly_plan_id" },
    color: "gold",
    testCard: PRICING.premiumCard,
    testBtn: { monthly: PRICING.subscribeMonthlyBtn, yearly: PRICING.subscribeYearlyBtn },
  },
  family: {
    label: "Family",
    eyebrow: "DreamWeaver Family",
    monthlyKey: "dreamweaver_family_monthly",
    yearlyKey: "dreamweaver_family_yearly",
    monthlyPrice: "$12",
    yearlyPrice: "$108",
    planIdField: { monthly: "family_monthly_plan_id", yearly: "family_yearly_plan_id" },
    color: "blue",
    testCard: PRICING.familyCard,
    testBtn: { monthly: PRICING.subscribeFamilyMonthlyBtn, yearly: PRICING.subscribeFamilyYearlyBtn },
  },
};

function tierOf(lookupKey) {
  if (!lookupKey) return null;
  return lookupKey.includes("family") ? "family" : "premium";
}

function ConfettiBurst({ burstKey }) {
  const particles = useMemo(
    () => Array.from({ length: 10 }, (_, i) => ({
      id: i,
      angle: (i / 10) * Math.PI * 2,
      color: ["#FBBF24", "#F472B6", "#A78BFA", "#34D399"][i % 4],
    })),
    []
  );
  if (!burstKey) return null;
  return (
    <AnimatePresence>
      <motion.span
        key={burstKey}
        style={{ position: "absolute", inset: 0 }}
        initial={false}
        exit={{ opacity: 0 }}
      >
        {particles.map((p) => (
          <motion.span
            key={`${burstKey}-${p.id}`}
            initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            animate={{ opacity: 0, x: Math.cos(p.angle) * 34, y: Math.sin(p.angle) * 34, scale: 0 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            style={{
              position: "absolute", width: 5, height: 5, borderRadius: "50%",
              background: p.color, top: "50%", left: "50%", pointerEvents: "none",
            }}
          />
        ))}
      </motion.span>
    </AnimatePresence>
  );
}

export default function PricingSection({ user, onAuthRequired, onSubscribed }) {
  const [billing, setBilling] = useState("monthly");
  const [payProvider, setPayProvider] = useState("paypal");
  const [loadingTier, setLoadingTier] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState("");
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [configError, setConfigError] = useState("");
  const [burstKey, setBurstKey] = useState(0);

  useEffect(() => {
    axios.get(`${API}/payments/paypal/config`)
      .then((res) => setPaypalConfig(res.data))
      .catch(() => setConfigError("Payments are temporarily unavailable. Please try again shortly."));
  }, []);

  useEffect(() => {
    if (billing === "yearly") {
      setBurstKey((k) => k + 1);
      const t = setTimeout(() => setBurstKey(0), 700);
      return () => clearTimeout(t);
    }
  }, [billing]);

  const currentTier = tierOf(user?.subscription_provider === "stripe" ? user?.stripe_lookup_key : user?.paypal_lookup_key);

  const handleSwitch = async (tierName, targetLookupKey, targetLabel) => {
    setLoadingTier(tierName);
    setSwitching(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/subscription/switch`, { lookup_key: targetLookupKey }, { withCredentials: true });
      if (data.approval_url) {
        window.location.href = data.approval_url;
        return;
      }
      onSubscribed?.();
    } catch (err) {
      setError(err.response?.data?.detail || `Could not switch to ${targetLabel}. Please try again.`);
    } finally {
      setLoadingTier(null);
      setSwitching(false);
    }
  };

  const handleStripeCheckout = async (tierName, targetLookupKey, targetLabel) => {
    setLoadingTier(tierName);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/payments/stripe/checkout`,
        { lookup_key: targetLookupKey, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || `Could not start checkout for ${targetLabel}. Please try again.`);
      setLoadingTier(null);
    }
  };

  const renderCta = (tierName) => {
    const meta = TIER_META[tierName];
    const lookupKey = billing === "monthly" ? meta.monthlyKey : meta.yearlyKey;
    const priceLabel = billing === "monthly" ? meta.monthlyPrice : meta.yearlyPrice;
    const periodLabel = billing === "monthly" ? "/month" : "/year";
    const planIdField = meta.planIdField[billing];
    const testId = meta.testBtn[billing];

    if (user?.is_subscribed && currentTier === tierName) {
      return (
        <div data-testid={testId} className="w-full py-3 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-medium text-center flex items-center justify-center gap-2">
          <Check size={14} /> You're a {meta.label} member
        </div>
      );
    }

    if (!user) {
      return (
        <button
          data-testid={testId}
          onClick={onAuthRequired}
          className="relative w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-400 text-black font-bold text-sm hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-200 hover:-translate-y-0.5 flex items-center justify-center gap-2"
        >
          <Star size={13} className="fill-black" /> Start {meta.label} — {priceLabel}{periodLabel}
        </button>
      );
    }

    if (loadingTier === tierName) {
      return (
        <div data-testid="paypal-confirming" className="w-full py-3.5 flex items-center justify-center gap-2 text-sm text-amber-300">
          <Loader2 size={16} className="animate-spin" /> {switching ? "Switching plans..." : "Confirming your subscription..."}
        </div>
      );
    }

    if (user.is_subscribed && currentTier && currentTier !== tierName) {
      return (
        <button
          data-testid={testId}
          onClick={() => handleSwitch(tierName, lookupKey, meta.label)}
          disabled={!!loadingTier}
          className="w-full py-3.5 rounded-xl border border-amber-400/40 text-amber-300 font-semibold text-sm hover:bg-amber-500/10 transition-all disabled:opacity-50"
        >
          Switch to {meta.label} — {priceLabel}{periodLabel}
        </button>
      );
    }

    if (user.is_subscribed && !currentTier) {
      return (
        <div data-testid={testId} className="w-full py-3 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-medium text-center flex items-center justify-center gap-2">
          <Check size={14} /> Included with your account
        </div>
      );
    }

    if (payProvider === "paypal" && !paypalConfig) {
      return (
        <p data-testid="paypal-config-error" className="text-center text-xs text-slate-500 py-3">
          {configError || "Loading payment options..."}
        </p>
      );
    }

    if (payProvider === "stripe") {
      return (
        <div data-testid={`${testId}-stripe`} className="min-h-[48px]">
          <button
            data-testid={`${testId}-stripe-btn`}
            onClick={() => handleStripeCheckout(tierName, lookupKey, meta.label)}
            disabled={!!loadingTier}
            className="relative w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-400 text-black font-bold text-sm hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-200 hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Star size={13} className="fill-black" /> Subscribe with Card — {priceLabel}{periodLabel}
          </button>
        </div>
      );
    }

    return (
      <div data-testid={testId} className="min-h-[48px]">
        <PayPalScriptProvider options={{ clientId: paypalConfig.client_id, vault: true, intent: "subscription", "disable-funding": "card,credit" }}>
          <PayPalButtons
            style={{ layout: "horizontal", color: meta.color, shape: "pill", label: "subscribe", height: 48, tagline: false }}
            forceReRender={[billing, tierName]}
            createSubscription={(data, actions) => actions.subscription.create({ plan_id: paypalConfig[planIdField], custom_id: user._id })}
            onApprove={async (data) => {
              setLoadingTier(tierName);
              setError("");
              try {
                await axios.post(
                  `${API}/payments/paypal/subscription-confirm`,
                  { subscription_id: data.subscriptionID, lookup_key: lookupKey },
                  { withCredentials: true }
                );
                onSubscribed?.();
              } catch {
                setError("We received your PayPal approval but couldn't confirm it — please refresh or contact support.");
              } finally {
                setLoadingTier(null);
              }
            }}
            onError={() => setError("PayPal checkout failed. Please try again.")}
            onCancel={() => setLoadingTier(null)}
          />
        </PayPalScriptProvider>
      </div>
    );
  };

  return (
    <section id="pricing" data-testid={PRICING.section} className="py-24 lg:py-36 relative">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 80% 50% at 50% 50%, rgba(109,40,217,0.08) 0%, transparent 70%)"
      }} />

      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <motion.span
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="inline-block text-xs font-mono tracking-[0.2em] uppercase text-amber-300/80 mb-4"
          >
            Pricing
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            One story is free.<br />
            <span className="gradient-text">Unlimited is $7/month.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-slate-400 text-sm max-w-md mx-auto"
          >
            Less than a picture book. More magical than anything on a screen.
          </motion.p>
        </div>

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="flex items-center justify-center gap-4 mb-12"
        >
          <span className={`text-sm ${billing === "monthly" ? "text-white" : "text-slate-500"}`}>Monthly</span>
          <button
            data-testid="billing-toggle"
            onClick={() => setBilling((b) => (b === "monthly" ? "yearly" : "monthly"))}
            className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
              billing === "yearly" ? "bg-amber-500" : "bg-purple-600/50"
            }`}
          >
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform duration-300 ${
              billing === "yearly" ? "translate-x-7" : "translate-x-1"
            }`} />
          </button>
          <span className={`text-sm flex items-center gap-2 ${billing === "yearly" ? "text-white" : "text-slate-500"}`}>
            Yearly
            <span className="relative px-1.5 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-300 border border-amber-400/30">
              Save 25%
              <ConfettiBurst burstKey={burstKey} />
            </span>
          </span>
        </motion.div>

        {error && (
          <p data-testid="pricing-error" className="text-center text-xs text-red-400 mb-6">{error}</p>
        )}

        {/* Payment Provider Toggle (only shown for a fresh subscribe, not when switching tiers) */}
        {!user?.is_subscribed && (
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="flex items-center justify-center gap-2 mb-10"
          >
            <span className="text-xs text-slate-500 mr-1">Pay with:</span>
            <button
              data-testid="pay-provider-paypal"
              onClick={() => setPayProvider("paypal")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                payProvider === "paypal" ? "bg-amber-500/20 border-amber-400/50 text-amber-300" : "border-purple-500/20 text-slate-400 hover:text-white"
              }`}
            >
              PayPal
            </button>
            <button
              data-testid="pay-provider-stripe"
              onClick={() => setPayProvider("stripe")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                payProvider === "stripe" ? "bg-amber-500/20 border-amber-400/50 text-amber-300" : "border-purple-500/20 text-slate-400 hover:text-white"
              }`}
            >
              Card (Stripe)
            </button>
          </motion.div>
        )}

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-6">
          {/* Free Card */}
          <motion.div
            data-testid={PRICING.freeCard}
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-8 border border-purple-500/20"
          >
            <div className="mb-6">
              <span className="text-xs font-mono tracking-widest uppercase text-slate-500 mb-2 block">Free Forever</span>
              <div className="flex items-end gap-2">
                <span className="font-serif text-4xl font-bold text-white">$0</span>
                <span className="text-slate-500 text-sm mb-1">to start</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">No credit card required</p>
            </div>

            <ul className="space-y-3 mb-8">
              {FREE_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-slate-400">
                  <Check size={13} className="text-purple-400 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => document.getElementById("generator")?.scrollIntoView({ behavior: "smooth" })}
              className="w-full py-3 rounded-xl border border-purple-500/30 text-slate-300 text-sm font-medium hover:border-purple-400/60 hover:text-white transition-all"
            >
              Try Free Story
            </button>
          </motion.div>

          {/* Premium Card */}
          <motion.div
            data-testid={PRICING.premiumCard}
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative rounded-2xl p-8 border border-amber-400/40 overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(22,18,48,0.9) 0%, rgba(30,20,60,0.9) 100%)" }}
          >
            <div className="absolute top-4 right-4">
              <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-semibold">
                <Crown size={11} /> Most Popular
              </span>
            </div>

            <div className="absolute inset-0 pointer-events-none" style={{
              background: "radial-gradient(ellipse 60% 60% at 50% 0%, rgba(245,158,11,0.12) 0%, transparent 70%)"
            }} />

            <div className="mb-6 relative">
              <span className="text-xs font-mono tracking-widest uppercase text-amber-300/80 mb-2 block">{TIER_META.premium.eyebrow}</span>
              <div className="flex items-end gap-2">
                <span className="font-serif text-4xl font-bold gradient-text-gold">
                  {billing === "monthly" ? TIER_META.premium.monthlyPrice : TIER_META.premium.yearlyPrice}
                </span>
                <span className="text-slate-400 text-sm mb-1">{billing === "monthly" ? "/ month" : "/ year"}</span>
              </div>
              {billing === "yearly" && (
                <p className="text-xs text-amber-400 mt-1">Save 25% vs monthly</p>
              )}
              {!user?.is_subscribed && (
                <p className="text-xs text-slate-500 mt-1">100% money-back guarantee</p>
              )}
            </div>

            <ul className="space-y-3 mb-8 relative">
              {PREMIUM_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-slate-300">
                  <Sparkles size={12} className="text-amber-400 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            {renderCta("premium")}

            <p className="mt-4 text-center text-xs text-slate-600">
              Secure payment via PayPal or Stripe · Cancel anytime
            </p>
          </motion.div>

          {/* Family Card */}
          <motion.div
            data-testid={PRICING.familyCard}
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="relative rounded-2xl p-8 border border-sky-400/30 overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(20,30,55,0.9) 100%)" }}
          >
            <div className="absolute top-4 right-4">
              <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-sky-500/20 border border-sky-400/30 text-sky-300 text-xs font-semibold">
                <Users size={11} /> For 2+ Kids
              </span>
            </div>

            <div className="absolute inset-0 pointer-events-none" style={{
              background: "radial-gradient(ellipse 60% 60% at 50% 0%, rgba(56,189,248,0.10) 0%, transparent 70%)"
            }} />

            <div className="mb-6 relative">
              <span className="text-xs font-mono tracking-widest uppercase text-sky-300/80 mb-2 block">{TIER_META.family.eyebrow}</span>
              <div className="flex items-end gap-2">
                <span className="font-serif text-4xl font-bold text-sky-300">
                  {billing === "monthly" ? TIER_META.family.monthlyPrice : TIER_META.family.yearlyPrice}
                </span>
                <span className="text-slate-400 text-sm mb-1">{billing === "monthly" ? "/ month" : "/ year"}</span>
              </div>
              {billing === "yearly" && (
                <p className="text-xs text-sky-400 mt-1">Save 25% vs monthly</p>
              )}
              {!user?.is_subscribed && (
                <p className="text-xs text-slate-500 mt-1">100% money-back guarantee</p>
              )}
            </div>

            <ul className="space-y-3 mb-8 relative">
              {FAMILY_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-slate-300">
                  <Sparkles size={12} className="text-sky-400 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            {renderCta("family")}

            <p className="mt-4 text-center text-xs text-slate-600">
              Secure payment via PayPal or Stripe · Cancel anytime
            </p>
          </motion.div>
        </div>

        {/* Guarantee */}
        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="text-center text-xs text-slate-600 mt-10"
        >
          We guarantee your child will love their first premium story, or we'll refund you completely — no questions asked.
        </motion.p>
      </div>
    </section>
  );
}
